import { FC, useMemo, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useParams } from 'react-router-dom';
import { useCompany, useGetCompanyHaloStock, useGetCustomerBundles } from '@/services/api/hooks/companies';
import { useCategories, useGetRateCards } from '@/services/api/hooks/catalog';
import { useProducts } from '@/services/api/hooks/product';
import { useCreateServiceRequest } from '@/services/api/hooks/serviceRequests';
import { useUser } from '@/services/authentication/hooks/user';
import { SelectInput } from '@/components/form/fields/select/SelectInput';
import { useModal } from '@/components/modal/useModal';
import { DeployInventoryDeliveryModal } from './DeployInventoryDeliveryModal';
import { Counter } from '@/components/form/fields/number/Counter';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { CategoryIcons, getCategoryIcon } from '@/components/icons/CategoryIcons';
import { ServiceRequestLayout } from '@/layouts/ServiceRequestLayout';
import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { Picture } from '@/components/image/Image';
import { HaloStockDto } from '@/services/api/contracts/companies';
import { Category } from '@/services/api/contracts/catalog/types';
import { useSandbox } from '../../contexts/SandboxContext';
import { RoleType } from '@/services/authentication/hooks/user';
import { useNavigate } from 'react-router-dom';
import { useDirtyFormGuard } from '@/hooks/useDirtyFormGuard';
import UnsavedChanges from '@/components/unsavedChanges/UnsavedChanges';
import { FiAlertTriangle } from 'react-icons/fi';

export const DeployInventoryPage: FC = () => {
  const { partnerId = '', companyId = '' } = useParams<{ partnerId: string; companyId?: string }>();

  if (!(partnerId && companyId)) throw new Error('Missing params');
  
  const navigate = useNavigate();
  const user = useUser();
  const isAdmin = user?.roles?.some((r: any) => r.type === RoleType.Admin);
  const { isSandboxMode, setTelemetry } = useSandbox();

  const [company] = useCompany(partnerId, companyId);
  const [rawHaloStock] = useGetCompanyHaloStock(partnerId, companyId);
  
  const aggregatedHaloStock = useMemo(() => {
    if (!rawHaloStock) return undefined;
    const aggregated = new Map<string, typeof rawHaloStock[0]>();
    for (const item of rawHaloStock) {
      if (aggregated.has(item.sku)) {
        const existing = aggregated.get(item.sku)!;
        existing.stockQuantity += item.stockQuantity;
        if (item.stockLocation) {
          const existingLocs = existing.stockLocation ? existing.stockLocation.split(' | ') : [];
          if (!existingLocs.includes(item.stockLocation)) {
            existing.stockLocation = existing.stockLocation ? `${existing.stockLocation} | ${item.stockLocation}` : item.stockLocation;
          }
        }
      } else {
        aggregated.set(item.sku, { ...item });
      }
    }
    return Array.from(aggregated.values());
  }, [rawHaloStock]);
  const [bundles] = useGetCustomerBundles(partnerId, companyId);
  const [rateCards] = useGetRateCards('DeployInventory', partnerId, companyId);
  const filteredRateCards = useMemo(() => {
    return rateCards?.items?.filter(rc => rc.propertyBag?.applicableRequestTypes?.includes('DeployInventory')) || [];
  }, [rateCards]);
  const [catalogProducts] = useProducts();
  const [categories] = useCategories();
  
  const createServiceRequest = useCreateServiceRequest();
  const deliveryModalProps = useModal();

  const [cart, setCart] = useState<Record<string, number>>({});
  const [selectedBundleId, setSelectedBundleId] = useState<string | null>(null);
  const [isGridExpanded, setIsGridExpanded] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [missingStockWarnings, setMissingStockWarnings] = useState<{ itemName: string; missingQty: number; sku?: string }[]>([]);
  const [backorderItems, setBackorderItems] = useState<Array<{ name: string; quantity: number; sku?: string }>>([]);

  const bundleOptions = useMemo(() => {
    const options = (bundles || []).map(b => ({ id: b.id, name: b.name }));
    return [{ id: '', name: '--- No Bundle (Manual Mode) ---' }, ...options];
  }, [bundles]);

  const groupedStock = useMemo(() => {
    const groups: Record<string, NonNullable<typeof aggregatedHaloStock>> = {};
    if (!aggregatedHaloStock) return groups;
    for (const item of aggregatedHaloStock) {
      const type = item.itemType || 'Other';
      if (!groups[type]) groups[type] = [];
      groups[type].push(item);
    }
    return groups;
  }, [aggregatedHaloStock]);

  const simulateCartWeight = (simulatedCart: Record<string, number>, simulatedBackorders: Array<{ name: string; quantity: number; sku?: string }> = []) => {
    let totalDeadWeight = 0;
    let totalVolumetricWeight = 0;

    const processItem = (sku: string | undefined, quantity: number, itemName?: string) => {
      if (quantity <= 0) return;
      
      let category;
      if (sku) {
        const stockItem = aggregatedHaloStock?.find(s => s.sku === sku);
        if (stockItem) {
          category = categories?.find(c => {
            let cName = c.name.toLowerCase().trim();
            if (cName.endsWith('s')) cName = cName.slice(0, -1);
            let nt = (stockItem.itemType || '').toLowerCase().trim();
            if (nt.endsWith('s')) nt = nt.slice(0, -1);
            return cName === nt;
          });
        }
      }

      if (!category && itemName) {
        category = categories?.find(c => c.name.toLowerCase() === itemName.toLowerCase());
      }

      if (category) {
        if (category.logisticsConfig?.deploy?.overrideRateCardId) {
          // This item is handled by an override crate fee, so it does not contribute to generic volumetric shipping
          return;
        }
        for (let i = 0; i < quantity; i++) {
          totalDeadWeight += (category.logisticsConfig?.deploy?.averageWeightKg || 0);
          const baseVolWeight = (category.logisticsConfig?.deploy?.dimensionalWeightKg || 0);
          const packingEff = category.logisticsConfig?.deploy?.packingEfficiencyPercent || 100;
          const packedVolWeight = baseVolWeight / (packingEff / 100);
          totalVolumetricWeight += packedVolWeight;
        }
      }
    };

    Object.entries(simulatedCart).forEach(([sku, quantity]) => {
      processItem(sku, quantity);
    });

    simulatedBackorders.forEach(item => {
      processItem(item.sku, item.quantity, item.name);
    });

    return Math.max(totalDeadWeight, totalVolumetricWeight);
  };

  const getMaxAllowedWeight = () => {
    const tierCards = filteredRateCards?.filter(c => c.propertyBag.maxChargeableWeightKg !== undefined && !c.propertyBag.isExclusiveOverride)
      .sort((a, b) => a.propertyBag.maxChargeableWeightKg! - b.propertyBag.maxChargeableWeightKg!) || [];
    if (tierCards.length > 0) {
      return tierCards[tierCards.length - 1].propertyBag.maxChargeableWeightKg!;
    }
    return Infinity;
  };

  const handleBundleChange = (property: { id: string; name: string } | null) => {
    const bundleId = property?.id;
    if (!bundleId) {
      setSelectedBundleId(null);
      setMissingStockWarnings([]);
      setBackorderItems([]);
      setIsGridExpanded(false);
      return;
    }
    
    setSelectedBundleId(bundleId);
    setMissingStockWarnings([]);
    setBackorderItems([]);
    setIsGridExpanded(false);
    const bundle = bundles?.find(b => b.id === bundleId);
    if (!bundle) return;

    const newCart: Record<string, number> = {};
    const newWarnings: { itemName: string; missingQty: number; sku?: string }[] = [];
    const newBackorderItems: Array<{ name: string; quantity: number; sku?: string }> = [];
    
    const requiredItems = [...(bundle.bundledItems || [])];
    if (bundle.mainDevice) {
      requiredItems.push({ catalogItem: bundle.mainDevice, count: 1 });
    }

    // Iterate through bundle items and find matching halo stock
    requiredItems.forEach(item => {
      let remainingQuantity = item.count;
      
      const bundleCategory = categories?.find(c => c.id === item.catalogItem);
      const bundleProduct = catalogProducts?.find(p => p.productId === item.catalogItem || p.id === item.catalogItem);
      
      const matchingStock = (aggregatedHaloStock || []).filter(s => {
        if (s.stockQuantity <= 0) return false;
        
        if (bundleCategory) {
          return s.itemType?.toLowerCase() === bundleCategory.name?.toLowerCase();
        }
        
        if (bundleProduct) {
          return s.sku === bundleProduct.code || s.sku === bundleProduct.productId;
        }
        
        return false;
      });
      
      for (const stock of matchingStock) {
        if (remainingQuantity <= 0) break;
        const currentCartQty = newCart[stock.sku] || 0;
        const availableToAdd = stock.stockQuantity - currentCartQty;
        
        if (availableToAdd > 0) {
          const qtyToAdd = Math.min(availableToAdd, remainingQuantity);
          newCart[stock.sku] = currentCartQty + qtyToAdd;
          remainingQuantity -= qtyToAdd;
        }
      }
      
      if (remainingQuantity > 0) {
        newWarnings.push({
          itemName: bundleProduct?.name || bundleCategory?.name || 'Unknown Item',
          missingQty: remainingQuantity,
          sku: bundleProduct?.productId || bundleProduct?.code
        });
        newBackorderItems.push({
          name: bundleProduct?.name || bundleCategory?.name || 'Unknown Item',
          quantity: remainingQuantity,
          sku: bundleProduct?.productId || bundleProduct?.code
        });
      }
    });

    const maxAllowedWeight = getMaxAllowedWeight();
    if (simulateCartWeight(newCart, newBackorderItems) > maxAllowedWeight) {
      toast.error('Bundle exceeds maximum shipping weight');
      setSelectedBundleId(null);
      return;
    }

    setCart(newCart);
    setMissingStockWarnings(newWarnings);
    setBackorderItems(newBackorderItems);
  };

  const handleCartChange = (sku: string, qty: number) => {
    setCart(prev => {
      const currentQty = prev[sku] || 0;
      if (qty > currentQty) {
        const simulatedCart = { ...prev, [sku]: qty };
        if (simulateCartWeight(simulatedCart, backorderItems) > getMaxAllowedWeight()) {
          toast.error('Max Weight Reached: Cannot exceed courier limits');
          return prev;
        }
      }
      return {
        ...prev,
        [sku]: qty
      };
    });
  };

  const { invoiceItems, telemetryData } = useMemo(() => {
    if (!aggregatedHaloStock || !filteredRateCards) return { invoiceItems: [], telemetryData: { deployment: { itemLog: [], totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenSkus: [] } } };
    
    const items: Array<{ name: string; itemType: string; quantity: number; price: number; total: number; isInventoryItem?: boolean; associatedPhysicalItems?: string[]; listSection: 'admin' | 'logistics' | 'processing' }> = [];
    const deploymentItemLog: any[] = [];

    const hasItems = Object.values(cart).some(qty => qty > 0);
    if (!hasItems) return { invoiceItems: items, telemetryData: { deployment: { itemLog: [], totalDeadWeight: 0, totalVolumetricWeight: 0, chargeableWeight: 0, overriddenSkus: [] } } };

    // Pass 1: Base Pack
    const basePack = filteredRateCards.find(c => c.propertyBag.basePackForRequestType === 'DeployInventory');
    if (basePack) {
      const rawPrice = basePack.price;
      const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
      const price = priceInPence / 100;
      
      items.push({
        name: basePack.name || 'Base Pack',
        itemType: 'Service',
        quantity: 1,
        price,
        total: price,
        isInventoryItem: false,
        listSection: 'admin'
      });
    }

    // Pass 2: Per-Item Handling
    const specificHandlingGroups: Record<string, { items: string[], totalQty: number }> = {};
    const genericFallbackItems: string[] = [];
    let genericHandlingQty = 0;

    Object.entries(cart).forEach(([sku, quantity]) => {
      if (quantity <= 0) return;
      const stockItem = aggregatedHaloStock.find(s => s.sku === sku);
      if (!stockItem) return;

      const category = categories?.find(c => {
        let cName = c.name.toLowerCase().trim();
        if (cName.endsWith('s')) cName = cName.slice(0, -1);
        let nt = (stockItem.itemType || '').toLowerCase().trim();
        if (nt.endsWith('s')) nt = nt.slice(0, -1);
        return cName === nt;
      });

      const itemProcessingRateCardId = category?.logisticsConfig?.deploy?.itemProcessingRateCardId;

      if (itemProcessingRateCardId) {
        if (!specificHandlingGroups[itemProcessingRateCardId]) {
          specificHandlingGroups[itemProcessingRateCardId] = { items: [], totalQty: 0 };
        }
        for (let i = 0; i < quantity; i++) {
          specificHandlingGroups[itemProcessingRateCardId].items.push(stockItem.item);
        }
        specificHandlingGroups[itemProcessingRateCardId].totalQty += quantity;
      } else {
        for (let i = 0; i < quantity; i++) {
          genericFallbackItems.push(stockItem.item);
        }
        genericHandlingQty += quantity;
      }
    });

    Object.entries(specificHandlingGroups).forEach(([rateCardId, group]) => {
      const specificCard = filteredRateCards.find(r => r.id === rateCardId);
      if (specificCard && group.totalQty > 0) {
        const rawPrice = specificCard.price;
        const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
        const price = priceInPence / 100;

        items.push({
          name: specificCard.name,
          itemType: 'Service',
          quantity: group.totalQty,
          price,
          total: price * group.totalQty,
          isInventoryItem: false,
          associatedPhysicalItems: group.items,
          listSection: 'processing'
        });
      }
    });

    if (genericHandlingQty > 0) {
      const handlingCard = filteredRateCards.find(c => c.propertyBag.itemRateForRequestType === 'DeployInventory');
      if (handlingCard) {
        const rawPrice = handlingCard.price;
        const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
        const price = priceInPence / 100;

        items.push({
          name: handlingCard.name || 'Handling',
          itemType: 'Service',
          quantity: genericHandlingQty,
          price,
          total: price * genericHandlingQty,
          isInventoryItem: false,
          associatedPhysicalItems: genericFallbackItems,
          listSection: 'processing'
        });
      }
    }

    // Pass 2.5: Override Extract
    const overriddenSkus: Set<string> = new Set();
    Object.entries(cart).forEach(([sku, quantity]) => {
      if (quantity <= 0) return;
      const stockItem = aggregatedHaloStock.find(s => s.sku === sku);
      if (!stockItem) return;
      
      const category = categories?.find(c => {
        let cName = c.name.toLowerCase().trim();
        if (cName.endsWith('s')) cName = cName.slice(0, -1);
        let nt = (stockItem.itemType || '').toLowerCase().trim();
        if (nt.endsWith('s')) nt = nt.slice(0, -1);
        return cName === nt;
      });

      if (category?.logisticsConfig?.deploy?.overrideRateCardId) {
        overriddenSkus.add(sku);
        const overrideCard = filteredRateCards.find(r => r.id === category.logisticsConfig!.deploy!.overrideRateCardId);
        if (overrideCard) {
          const rawPrice = overrideCard.price;
          const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
          const price = priceInPence / 100;
          
          const overrideItems = [];
          for (let i = 0; i < quantity; i++) {
            overrideItems.push(stockItem.item);
          }
          
          items.push({
            name: overrideCard.name || 'Crate Fee',
            itemType: 'Service',
            quantity,
            price,
            total: price * quantity,
            isInventoryItem: false,
            associatedPhysicalItems: overrideItems,
            listSection: 'logistics'
          });
        }
      }
    });

    // Pass 3: Volumetric Shipping Tier
    let totalDeadWeight = 0;
    let totalVolumetricWeight = 0;
    const overriddenDeploySkus = new Set(Object.values(specificHandlingGroups).flatMap(g => g.items));

    const processLogisticsItem = (sku: string | undefined, quantity: number, itemName?: string) => {
      if (quantity <= 0) return;
      const isOverridden = sku ? (overriddenDeploySkus.has(sku) || overriddenDeploySkus.has(aggregatedHaloStock?.find(s => s.sku === sku)?.item || '')) : false;
      
      let category;
      let stockItem: any;
      if (sku) {
        stockItem = aggregatedHaloStock?.find(s => s.sku === sku);
        if (stockItem) {
          category = categories?.find(c => {
            let cName = c.name.toLowerCase().trim();
            if (cName.endsWith('s')) cName = cName.slice(0, -1);
            let nt = (stockItem?.itemType || '').toLowerCase().trim();
            if (nt.endsWith('s')) nt = nt.slice(0, -1);
            return cName === nt;
          });
        }
      }

      if (!category && itemName) {
        category = categories?.find(c => c.name.toLowerCase() === itemName.toLowerCase());
      }

      let deadWt = 0;
      let baseVol = 0;
      let packEff = 100;
      let packedVol = 0;

      if (category) {
        deadWt = (category.logisticsConfig?.deploy?.averageWeightKg || 0);
        baseVol = (category.logisticsConfig?.deploy?.dimensionalWeightKg || 0);
        packEff = (category.logisticsConfig?.deploy?.packingEfficiencyPercent || 100);
        packedVol = baseVol / (packEff / 100);

        if (!isOverridden) {
          totalDeadWeight += (deadWt * quantity);
          totalVolumetricWeight += (packedVol * quantity);
        }
      }

      deploymentItemLog.push({
        name: stockItem?.item || itemName || sku || 'Unknown',
        quantity,
        deadWeightKg: deadWt,
        baseVolumetricKg: baseVol,
        packingEfficiency: packEff,
        packedVolumetricKg: packedVol,
        isOverridden
      });
    };

    Object.entries(cart).forEach(([sku, quantity]) => {
      processLogisticsItem(sku, quantity);
    });

    backorderItems.forEach(item => {
      processLogisticsItem(item.sku, item.quantity, item.name);
    });

    const chargeableWeight = Math.max(totalDeadWeight, totalVolumetricWeight);
    const telemetryData = {
      deployment: {
        totalDeadWeight,
        totalVolumetricWeight,
        chargeableWeight,
        overriddenSkus: Array.from(overriddenDeploySkus),
        itemLog: deploymentItemLog
      }
    };

    const tierCards = filteredRateCards.filter(c => c.propertyBag.maxChargeableWeightKg !== undefined && !c.propertyBag.isExclusiveOverride)
      .sort((a, b) => a.propertyBag.maxChargeableWeightKg! - b.propertyBag.maxChargeableWeightKg!);

    const matchingTier = tierCards.find(c => c.propertyBag.maxChargeableWeightKg! >= chargeableWeight);
    if (matchingTier) {
      const rawPrice = matchingTier.price;
      const priceInPence = typeof rawPrice === 'number' ? rawPrice : rawPrice?.price || 0;
      const price = priceInPence / 100;

      items.push({
        name: matchingTier.name || 'Shipping Tier',
        itemType: 'Service',
        quantity: 1,
        price,
        total: price,
        isInventoryItem: false,
        listSection: 'logistics'
      });
    }

    return { invoiceItems: items, telemetryData };
  }, [cart, aggregatedHaloStock, filteredRateCards, categories]);

  const deployedHardware = useMemo(() => {
    return Object.entries(cart)
      .filter(([_, qty]) => qty > 0)
      .map(([sku, qty]) => {
        const stockItem = aggregatedHaloStock?.find(s => s.sku === sku);
        return {
          name: stockItem?.item || sku,
          quantity: qty,
          sku: sku
        };
      });
  }, [cart, aggregatedHaloStock]);

  const bundleHardware = useMemo(() => {
    if (!selectedBundleId || !bundles) return [];
    const bundle = bundles.find(b => b.id === selectedBundleId);
    if (!bundle) return [];
    
    const requiredItems = [...(bundle.bundledItems || [])];
    if (bundle.mainDevice) {
      requiredItems.push({ catalogItem: bundle.mainDevice, count: 1 });
    }

    const bundleSkus = new Set<string>();
    requiredItems.forEach(item => {
      const bundleProduct = catalogProducts?.find(p => p.productId === item.catalogItem || p.id === item.catalogItem);
      if (bundleProduct) {
        const sku = bundleProduct.productId || bundleProduct.code;
        if (sku) bundleSkus.add(sku.toLowerCase());
      }
    });

    return deployedHardware.filter(item => item.sku && bundleSkus.has(item.sku.toLowerCase()));
  }, [selectedBundleId, bundles, catalogProducts, deployedHardware]);

  const telemetryString = JSON.stringify(telemetryData);
  useEffect(() => {
    if (isSandboxMode && telemetryString) {
      setTelemetry(JSON.parse(telemetryString));
    }
  }, [telemetryString, isSandboxMode, setTelemetry]);

  const totalCost = invoiceItems.reduce((acc, item) => acc + item.total, 0);
  const currencyFormatter = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' });

  const onSubmit = async () => {
    deliveryModalProps.open();
  };

  const resetForm = useCallback(() => {
    setCart({});
    setSelectedBundleId(null);
    setMissingStockWarnings([]);
    setBackorderItems([]);
    setIsGridExpanded(false);
    setSubmitError(null);
  }, []);

  const breadcrumbs: NavPage[] = [
    { name: 'Management', to: '' },
    { name: 'Deploy Inventory', to: '' }
  ];

  const isDirty = useMemo(() => {
    return Object.values(cart).some(qty => qty > 0) || !!selectedBundleId;
  }, [cart, selectedBundleId]);

  useDirtyFormGuard(isDirty);

  return (
    <>
      <UnsavedChanges 
        show={isDirty} 
        title="Abandon Request"
        message="Your request is partially complete. Are you sure you want to leave? Your progress will be lost."
        confirmText="Yes, Abandon"
        confirmColorClass="bg-secondary-600 hover:bg-secondary-700 text-white"
        cancelText="No, Continue"
        cancelColorClass="bg-primary-700 hover:bg-primary-800 text-white"
      />
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between items-start w-full">
          <div className="flex flex-col gap-2 items-start">
            <h3 className="text-3xl font-bold">Deploy Inventory</h3>
            <p className="font-medium">Select a bundle or add hardware to deploy.</p>
          </div>
          {isAdmin && (
            isSandboxMode ? (
              <button
                type="button"
                onClick={() => navigate(`/msps/${partnerId}/companies/${companyId}/management/deploy-inventory`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 rounded-md transition-colors border border-red-200 shadow-sm"
              >
                <span>🚪</span> Exit Sandbox
              </button>
            ) : (
              <button
                type="button"
                onClick={() => navigate(`/admin/sandbox/partners/${partnerId}/companies/${companyId}/deploy-inventory`)}
                className="flex items-center gap-2 px-3 py-1.5 text-sm font-mono text-gray-500 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors border border-gray-200 shadow-sm"
              >
                <span>⚙️</span> Debug in Sandbox
              </button>
            )
          )}
        </div>
      </BreadcrumbPageHeader>
      
      <ServiceRequestLayout
        title=""
        macroSlot={
          <div className="bg-white p-4 lg:p-6 rounded-lg shadow-sm border border-gray-200">
            <h2 className="text-lg font-bold mb-2">Quick Deploy Bundle</h2>
            <div className="max-w-md">
              <SelectInput
                id="bundle-select"
                options={bundleOptions}
                value={bundleOptions.find(o => o.id === selectedBundleId) || null}
                onChange={handleBundleChange}
                placeholder="Select a bundle to autofill..."
              />
            </div>
          </div>
        }
        leftColumn={
          <div className="flex flex-col gap-8">
            {missingStockWarnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 text-amber-800 p-4 rounded-md">
                <h4 className="font-bold mb-2 flex items-center gap-2">
                  <FiAlertTriangle className="w-5 h-5" /> 
                  Missing stock items warning: will be on backorder
                </h4>
                <ul className="list-disc pl-6 text-sm">
                  {missingStockWarnings.map((w, idx) => (
                    <li key={idx}>
                      {w.missingQty}x {w.itemName} {w.sku && <span className="opacity-75 text-xs ml-1">({w.sku})</span>} required but out of stock
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!!selectedBundleId && (
              <div className="border border-blue-500 rounded-lg bg-white overflow-hidden p-4">
                <h3 className="text-lg font-bold mb-4">{bundles?.find(b => b.id === selectedBundleId)?.name || 'Bundle'} Stock Available for Deployment:</h3>
                <div className="flex flex-col gap-4">
                  {bundleHardware && bundleHardware.length > 0 ? (
                    bundleHardware.map((item) => (
                      <div key={item.sku} className="flex justify-between items-center text-sm border-b pb-2 last:border-0 last:pb-0">
                        <div>
                          <span className="font-medium text-gray-900">{item.name}</span>
                          <span className="text-gray-500 block text-xs">SKU: {item.sku}</span>
                        </div>
                        <div className="font-semibold text-gray-700">{item.quantity}x</div>
                      </div>
                    ))
                  ) : (
                    <div className="text-gray-500 italic text-sm py-2">
                      All hardware in the selected bundle will require backorder.
                    </div>
                  )}
                </div>
                {!isGridExpanded && (
                  <div className="mt-6 border-t pt-4">
                    <Button variant={ButtonVariant.Secondary} onClick={() => setIsGridExpanded(true)}>
                      Add / Remove Products (Manual Mode)
                    </Button>
                  </div>
                )}
              </div>
            )}

            {(!selectedBundleId || isGridExpanded) && (
              <>
                {Object.entries(groupedStock).map(([itemType, items]) => (
                  <div key={itemType}>
                    <h2 className="text-xl font-bold mb-4 border-b pb-2">{itemType}</h2>
                    <div className="flex flex-col border border-gray-200 rounded-lg bg-white overflow-hidden">
                      {items.map((item, idx) => {
                        const currentQty = cart[item.sku] || 0;
                        
                        // Strict Matching for Image
                        const matchingCatalogProduct = catalogProducts?.find(
                          p => p.productId === item.sku || p.code === item.sku
                        );
                        const hasImage = !!matchingCatalogProduct?.imageUri?.id;
                        
                        // Map itemType to catalog category for icon fallback
                        const fallbackCategory = categories?.find(c => c.name.toLowerCase() === (item.itemType || '').toLowerCase());
                        const iconKey = matchingCatalogProduct?.category?.description || fallbackCategory?.description || '';

                        const isBundleItem = !!selectedBundleId && currentQty > 0;

                        return (
                          <div key={`${item.sku}-${idx}`} className={`flex flex-row items-center justify-between p-2 lg:p-3 hover:bg-gray-50 ${isBundleItem ? 'border border-blue-500 bg-blue-50/30 relative z-10' : 'border-b border-gray-100 last:border-b-0'}`}>
                            
                            {/* Left Side (Image & Info) */}
                            <div className="flex flex-row items-center gap-4 flex-1 overflow-hidden">
                              <div className="w-12 h-12 flex items-center justify-center shrink-0">
                                {hasImage ? (
                                  <Picture image={matchingCatalogProduct.imageUri!} containerHeight={48} containerWidth={48} />
                                ) : (
                                <div className="w-8 h-8 text-gray-300 [&_svg]:w-full [&_svg]:h-full">
                                  {getCategoryIcon(iconKey)}
                                </div>
                                )}
                              </div>

                              <div className="flex flex-col">
                                <span className="text-sm font-semibold text-gray-900 line-clamp-1" title={item.item}>
                                  {item.item}
                                </span>
                                <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                                  <span>SKU: {item.sku}</span>
                                  <span>Loc: {item.stockLocation || 'N/A'}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Right Side (Controls) */}
                            <div className="ml-4 shrink-0 flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-700">Your Stock: {item.stockQuantity}</span>
                              <Counter
                                id={`counter-${item.sku}`}
                                value={currentQty}
                                min={0}
                                max={item.stockQuantity}
                                onChange={(val) => handleCartChange(item.sku, val)}
                                allowManualChange={false}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                
                {(!aggregatedHaloStock || aggregatedHaloStock.length === 0) && (
                  <div className="py-12 text-center text-gray-500 bg-white rounded-lg border border-dashed border-gray-300">
                    No warehouse stock available for this company.
                  </div>
                )}
              </>
            )}
          </div>
        }
        rightColumn={
          <div className="sticky top-4 space-y-4">
            <div className="border p-4 rounded bg-gray-50 shadow-sm space-y-4">
              <h4 className="text-xl font-bold border-b pb-2">Basket</h4>
            
            <div className="flex flex-col gap-4 min-h-[200px]">
              {(invoiceItems.length > 0 || backorderItems.length > 0) ? (
                <>
                  <div className="space-y-4 max-h-[50vh] overflow-y-auto pr-2">
                    {(() => {
                      const adminFees = invoiceItems.filter(s => s.listSection === 'admin');
                      const logisticsItems = invoiceItems.filter(s => s.listSection === 'logistics');
                      const processingItems = invoiceItems.filter(s => s.listSection === 'processing');

                      const renderService = (service: any, idx: string | number) => {
                        const physicalGroups = (service.associatedPhysicalItems || []).reduce((acc: Record<string, number>, name: string) => {
                          acc[name] = (acc[name] || 0) + 1;
                          return acc;
                        }, {});

                        return (
                          <div key={idx} className="p-3 bg-white border rounded">
                            <div className="flex justify-between items-start text-sm">
                              <div className="pr-4">
                                <span className="font-medium">{service.quantity}x</span> {service.name}
                              </div>
                              <div className="font-medium whitespace-nowrap">
                                {service.isInventoryItem ? <span className="italic text-gray-500">From Inv</span> : currencyFormatter.format(service.total)}
                              </div>
                            </div>

                            {Object.keys(physicalGroups).length > 0 && (
                              <div className="mt-2 space-y-3 pt-2 border-t border-gray-100 pl-4 border-l-2 border-l-blue-200">
                                {Object.entries(physicalGroups).map(([name, count], groupIdx) => (
                                  <div key={groupIdx} className="space-y-2">
                                    <p className="text-sm font-medium text-gray-700">{`${count}x ${name}`}</p>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      };

                      return (
                        <>
                          {adminFees.length > 0 && (
                            <div className="space-y-3">
                              <h5 className="text-sm font-semibold text-gray-500 uppercase">Administration</h5>
                              {adminFees.map((service, idx) => renderService(service, `admin-${idx}`))}
                            </div>
                          )}
                          {logisticsItems.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-gray-200">
                              <h5 className="text-sm font-semibold text-gray-500 uppercase">Logistics</h5>
                              {logisticsItems.map((service, idx) => renderService(service, `logistics-${idx}`))}
                            </div>
                          )}
                          {processingItems.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-gray-200">
                              <h5 className="text-sm font-semibold text-gray-500 uppercase">Processing</h5>
                              {processingItems.map((service, idx) => renderService(service, `proc-${idx}`))}
                            </div>
                          )}
                          {backorderItems.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-gray-200">
                              <h5 className="text-sm font-semibold text-amber-600 uppercase">
                                On Backorder
                                <span className="text-xs italic font-normal normal-case text-gray-500 ml-2">additional fees may apply</span>
                              </h5>
                              {backorderItems.map((item, idx) => (
                                <div key={`backorder-${idx}`} className="p-3 bg-amber-50 border border-amber-200 rounded">
                                  <div className="flex justify-between items-start text-sm">
                                    <div className="pr-4 text-amber-800 flex flex-col">
                                      <span><span className="font-medium">{item.quantity}x</span> {item.name}</span>
                                      {item.sku && <span className="text-xs opacity-75 ml-[1.6rem]">SKU: {item.sku}</span>}
                                    </div>

                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4 mt-2">
                    <div className="flex justify-between items-center text-lg font-bold text-gray-900 mb-6">
                      <span>Total Estimate:</span>
                      <span>{currencyFormatter.format(totalCost)}</span>
                    </div>
                    
                    {submitError && (
                      <div className="text-red-600 text-sm mb-4 bg-red-50 p-3 rounded border border-red-200">
                        {submitError}
                      </div>
                    )}

                    <div className="flex flex-col gap-2">
                      <Button 
                        variant={ButtonVariant.Primary} 
                        className="w-full justify-center" 
                        disabled={(totalCost <= 0 && invoiceItems.length === 0 && backorderItems.length === 0) || createServiceRequest.isLoading}
                        onClick={onSubmit}
                      >
                        {createServiceRequest.isLoading ? 'Submitting...' : 'Submit Request'}
                      </Button>
                      <Button 
                        variant={ButtonVariant.Secondary} 
                        className="w-full justify-center" 
                        onClick={resetForm}
                        disabled={createServiceRequest.isLoading}
                      >
                        Clear Form
                      </Button>
                    </div>
                  </div>
                </>
              ) : (
                <div className="flex flex-col items-center justify-center flex-1 text-gray-400 py-8">
                  <p className="text-sm">Your basket is empty.</p>
                  <p className="text-xs mt-1 text-center">Add hardware from the catalog to estimate deployment costs.</p>
                </div>
              )}
            </div>
          </div>
          </div>
        }
      />
      <DeployInventoryDeliveryModal 
        {...deliveryModalProps} 
        mspId={partnerId} 
        customerId={companyId} 
        cart={cart}
        selectedBundleId={selectedBundleId}
        invoiceItems={invoiceItems}
        deployedHardware={deployedHardware}
        totalCost={totalCost}
        backorderItems={backorderItems}
        onSuccessCallback={() => {
          resetForm();
          toast.success('Service Request Submitted Successfully');
          setTimeout(() => {
            navigate(`/msps/${partnerId}/companies/${companyId}/management/submittedRequests`);
          }, 0);
        }}
      />
    </>
  );
};
